import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useChatMessages } from './useChatMessages';
import { useChatPresence } from './useChatPresence';
import { useChatReactions } from './useChatReactions';
import { useChat } from './ChatContext';
import MessageBubble, { formatSeparatorDate } from './MessageBubble';
import ChatReactions from './ChatReactions';
import MessageInput from './MessageInput';
import { Loader2, ArrowDown } from 'lucide-react';
import { differenceInMinutes, isSameDay } from 'date-fns';

interface MessageThreadProps {
  channelId: string;
}

export default function MessageThread({ channelId }: MessageThreadProps) {
  const { openThread } = useChat();
  const { messages, loading, error, loadMore, hasMore, sendMessage, editMessage, deleteMessage } =
    useChatMessages(channelId);
  const { typingUsers, broadcastTyping } = useChatPresence(channelId);
  const messageIds = useMemo(() => messages.map(m => m.id), [messages]);
  const { reactions, addReaction, removeReaction } = useChatReactions(messageIds);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const prevMessageCountRef = useRef(messages.length);
  const isLoadingMoreRef = useRef(false);

  // Check if user is scrolled to bottom
  const checkIsAtBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const threshold = 80;
    const atBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      threshold;
    setIsAtBottom(atBottom);
    if (atBottom) setHasNewMessages(false);
  }, []);

  // Scroll to bottom
  const scrollToBottom = useCallback((smooth = true) => {
    bottomRef.current?.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
    });
    setHasNewMessages(false);
  }, []);

  // Auto-scroll on new messages if at bottom
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const isOlderMessages = isLoadingMoreRef.current;
      isLoadingMoreRef.current = false;

      if (!isOlderMessages) {
        // New messages arrived
        if (isAtBottom) {
          scrollToBottom(false);
        } else {
          setHasNewMessages(true);
        }
      }
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length, isAtBottom, scrollToBottom]);

  // Initial scroll to bottom
  useEffect(() => {
    if (!loading && messages.length > 0) {
      scrollToBottom(false);
    }
  }, [loading, channelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle scroll to top for loading more
  const handleScroll = useCallback(() => {
    checkIsAtBottom();

    const container = scrollContainerRef.current;
    if (!container) return;

    // Load more when scrolled near top
    if (container.scrollTop < 100 && hasMore && !loading) {
      isLoadingMoreRef.current = true;
      const prevScrollHeight = container.scrollHeight;

      loadMore().then(() => {
        // Preserve scroll position after prepending older messages
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            const newScrollHeight = scrollContainerRef.current.scrollHeight;
            scrollContainerRef.current.scrollTop =
              newScrollHeight - prevScrollHeight;
          }
        });
      });
    }
  }, [checkIsAtBottom, hasMore, loading, loadMore]);

  // Determine if messages should be grouped
  function shouldGroup(
    current: (typeof messages)[0],
    previous: (typeof messages)[0] | undefined
  ): boolean {
    if (!previous) return false;
    if (current.user_id !== previous.user_id) return false;
    const diff = differenceInMinutes(
      new Date(current.created_at),
      new Date(previous.created_at)
    );
    return diff < 10;
  }

  function shouldShowDateSeparator(
    current: (typeof messages)[0],
    previous: (typeof messages)[0] | undefined
  ): boolean {
    if (!previous) return true;
    return !isSameDay(
      new Date(current.created_at),
      new Date(previous.created_at)
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Messages area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto chat-scrollbar"
      >
        {/* Loading indicator for older messages */}
        {hasMore && (
          <div className="flex justify-center py-3">
            <button
              onClick={() => {
                isLoadingMoreRef.current = true;
                loadMore();
              }}
              className="hover-wash pressable"
              style={{
                padding: '5px 12px',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-pill)',
                background: 'transparent',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              Load older messages
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-faint)' }} />
          </div>
        )}

        {/* Empty state */}
        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full px-6 text-center">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
              No messages yet — say hello.
            </p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="flex items-center justify-center h-full">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--danger-400)' }}>{error}</p>
          </div>
        )}

        {/* Messages */}
        <div className="py-2">
          {messages.map((msg, idx) => {
            const prevMsg = idx > 0 ? messages[idx - 1] : undefined;
            const isGrouped = shouldGroup(msg, prevMsg);
            const showDateSep = shouldShowDateSeparator(msg, prevMsg);

            // Show time separator for messages > 10 min apart (same user) or different day
            const showTimeSeparator =
              showDateSep ||
              (!isGrouped &&
                prevMsg &&
                differenceInMinutes(
                  new Date(msg.created_at),
                  new Date(prevMsg.created_at)
                ) > 30);

            return (
              <div key={msg.id} className="group">
                <MessageBubble
                  message={msg}
                  isGrouped={isGrouped && !showDateSep}
                  showTimeSeparator={!!showTimeSeparator}
                  timeSeparatorLabel={
                    showDateSep
                      ? formatSeparatorDate(msg.created_at)
                      : undefined
                  }
                  onEdit={editMessage}
                  onDelete={deleteMessage}
                  onReplyInThread={openThread}
                  onAddReaction={(messageId, emoji) => addReaction(messageId, emoji)}
                  replyCount={msg.reply_count}
                  lastReplyAt={msg.last_reply_at ?? undefined}
                />
                <ChatReactions
                  messageId={msg.id}
                  reactions={reactions.get(msg.id) || []}
                  onAdd={(emoji) => addReaction(msg.id, emoji)}
                  onRemove={(emoji) => removeReaction(msg.id, emoji)}
                />
              </div>
            );
          })}
        </div>

        {/* Typing indicator */}
        {typingUsers.length > 0 && (
          <div className="px-3.5 py-1">
            <span className="italic" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              {typingUsers.length === 1
                ? `${typingUsers[0].name} is typing...`
                : typingUsers.length === 2
                ? `${typingUsers[0].name} and ${typingUsers[1].name} are typing...`
                : 'Several people are typing...'}
            </span>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* New messages indicator */}
      {hasNewMessages && !isAtBottom && (
        <button
          onClick={() => scrollToBottom(true)}
          className="absolute bottom-16 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10 pressable ck-menu-pop"
          style={{
            padding: '5px 12px',
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-pill)',
            boxShadow: 'var(--shadow-pop)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
            color: 'var(--text-body)',
            cursor: 'pointer',
          }}
        >
          <ArrowDown className="w-3 h-3" />
          New messages
        </button>
      )}

      {/* Message input */}
      <MessageInput channelId={channelId} onSend={sendMessage} onTyping={broadcastTyping} />
    </div>
  );
}
